import type {QueryClient} from '@tanstack/react-query'

import {isQueryDataGrouped} from '../queries/query-data-helpers'
import {
  buildMemexGroupsQueryKey,
  buildMemexSecondaryGroupsQueryKey,
  type GroupId,
  pageTypeForGroups,
} from '../queries/query-keys'
import type {
  MemexGroupsPageQueryData,
  MemexPageQueryData,
  PageParam,
  PaginatedMemexItemsQueryVariables,
} from '../queries/types'
import {getKey} from './helpers'

/**
 * A thin wrapper around getQueryData for getting the memex groups data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're getting to be included in the query key
 */
export function getQueryDataForMemexGroupsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
): MemexGroupsPageQueryData | undefined {
  const queryKey = buildMemexGroupsQueryKey(variables, pageParam)
  return queryClient.getQueryData<MemexGroupsPageQueryData>(queryKey)
}

/**
 * A thin wrapper around setQueryData for setting the memex group data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're setting to be included in the query key
 * @param queryData The new groups data
 */
export function setQueryDataForMemexGroupsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
  queryData: MemexGroupsPageQueryData,
) {
  const queryKey = buildMemexGroupsQueryKey(variables, pageParam)
  queryClient.setQueryData<MemexGroupsPageQueryData>(queryKey, queryData)
}

/**
 * A thin wrapper around getQueryData for getting the memex secondary groups data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're getting to be included in the query key
 */
export function getQueryDataForMemexSecondaryGroupsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
): MemexGroupsPageQueryData | undefined {
  const queryKey = buildMemexSecondaryGroupsQueryKey(variables, pageParam)
  return queryClient.getQueryData<MemexGroupsPageQueryData>(queryKey)
}

/**
 * A thin wrapper around setQueryData for setting the memex secondary group data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're setting to be included in the query key
 * @param queryData The new secondary groups data
 */
export function setQueryDataForMemexSecondaryGroupsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
  queryData: MemexGroupsPageQueryData,
) {
  const queryKey = buildMemexSecondaryGroupsQueryKey(variables, pageParam)
  queryClient.setQueryData<MemexGroupsPageQueryData>(queryKey, queryData)
}

export type GroupIdToGroupValueMap = {[groupId: GroupId]: string}

/**
 * A helper building a map between groupId -> groupValue based on
 * data in the query client
 * @param queryClient
 */
export function buildGroupValuesFromQueryData(queryClient: QueryClient): GroupIdToGroupValueMap {
  const groupValuesMap: GroupIdToGroupValueMap = {}

  const queryKeyWithoutPageParamForGroups = [...getKey(queryClient), pageTypeForGroups]
  const queryPages = queryClient.getQueriesData<MemexPageQueryData>({queryKey: queryKeyWithoutPageParamForGroups})

  for (const [_queryKey, page] of queryPages) {
    const groups = page && isQueryDataGrouped(page) ? page.groups : []

    for (const group of groups) {
      groupValuesMap[group.groupId] = group.groupValue
    }
  }

  return groupValuesMap
}
